if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.iosScrollSnapTimer = null;

        if (
          !this.elements.viewer ||
          !this.elements.thumbnails
        ) {
          return;
        }

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(
            this.onSlideChanged.bind(this),
            500
          )
        );

        this.elements.thumbnails
          .querySelectorAll('[data-target]')
          .forEach((mediaToSwitch) => {
            const button =
              mediaToSwitch.querySelector('button');

            if (!button) return;

            button.addEventListener(
              'click',
              this.setActiveMedia.bind(
                this,
                mediaToSwitch.dataset.target,
                false
              )
            );
          });

        if (
          this.dataset.desktopLayout?.includes(
            'thumbnail'
          ) &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      onSlideChanged(event) {
        const currentElement =
          event.detail?.currentElement;

        if (!currentElement) return;

        const mediaId =
          currentElement.dataset.mediaId;

        if (!mediaId) return;

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.toggle(
              'is-active',
              element.dataset.mediaId === mediaId
            );
          });

        const thumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          ) ||
          this.elements.viewer.querySelector(
            '[data-media-id]'
          );

        if (!activeMedia) return;

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia.classList.add('is-active');

        const activeThumbnail =
          this.elements.thumbnails?.querySelector(
            `[data-target="${activeMedia.dataset.mediaId}"]`
          );

        if (prepend) {
          if (
            activeMedia.parentElement
              .firstElementChild !== activeMedia
          ) {
            activeMedia.parentElement.prepend(
              activeMedia
            );
          }

          if (
            activeThumbnail &&
            activeThumbnail.parentElement
              .firstElementChild !== activeThumbnail
          ) {
            activeThumbnail.parentElement.prepend(
              activeThumbnail
            );
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        const mediaSlider =
          this.elements.viewer.slider ||
          activeMedia.parentElement;

        const isIOSSafari =
          /iPad|iPhone|iPod/.test(
            navigator.userAgent
          ) ||
          (
            navigator.platform === 'MacIntel' &&
            navigator.maxTouchPoints > 1
          );

        /*
         * iOS Safari has a WebKit bug where
         * programmatic scrolling inside a scroll-snap
         * carousel can bounce back to the previous image.
         */
        if (isIOSSafari) {
          window.clearTimeout(
            this.iosScrollSnapTimer
          );

          const previousScrollSnapType =
            mediaSlider.style.scrollSnapType;

          mediaSlider.style.scrollSnapType = 'none';

          mediaSlider.scrollLeft =
            activeMedia.offsetLeft;

          this.iosScrollSnapTimer =
            window.setTimeout(() => {
              mediaSlider.style.scrollSnapType =
                previousScrollSnapType;

              mediaSlider.scrollLeft =
                activeMedia.offsetLeft;
            }, 400);
        } else {
          window.setTimeout(() => {
            mediaSlider.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: 'auto',
            });

            const activeMediaRect =
              activeMedia.getBoundingClientRect();

            if (activeMediaRect.top > -0.5) {
              return;
            }

            const top =
              activeMediaRect.top +
              window.scrollY;

            window.scrollTo({
              top,
              behavior: 'smooth',
            });
          });
        }

        this.playActiveMedia(activeMedia);

        if (!activeThumbnail) return;

        this.setActiveThumbnail(activeThumbnail);

        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );
      }

      setActiveThumbnail(thumbnail) {
        if (
          !this.elements.thumbnails ||
          !thumbnail
        ) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute(
              'aria-current'
            );
          });

        const button =
          thumbnail.querySelector('button');

        if (!button) return;

        button.setAttribute(
          'aria-current',
          'true'
        );

        if (
          this.elements.thumbnails
            .isSlideVisible(thumbnail, 10)
        ) {
          return;
        }

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail.offsetLeft,
          behavior: 'auto',
        });
      }

      announceLiveRegion(
        activeItem,
        position
      ) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (
          !image ||
          !this.elements.liveRegion
        ) {
          return;
        }

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            'false'
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings
              .imageAvailable
              .replace('[index]', position);

          window.setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              'true'
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector(
            '.deferred-media'
          );

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector(
            'sticky-header'
          );

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (
          !this.elements.viewer.slider
        ) {
          return;
        }

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) => {
            slide.setAttribute(
              'role',
              'presentation'
            );
          }
        );
      }
    }
  );
}

/* TRC: preserve gallery image for size changes */
(() => {
  const MediaGallery =
    customElements.get('media-gallery');

  if (
    !MediaGallery ||
    MediaGallery.prototype
      .trcVariantMediaGuardInstalled
  ) {
    return;
  }

  MediaGallery.prototype
    .trcVariantMediaGuardInstalled = true;

  const originalSetActiveMedia =
    MediaGallery.prototype.setActiveMedia;

  MediaGallery.prototype.setActiveMedia =
    function (mediaId, prepend) {
      /*
       * Thumbnail selections use prepend: false.
       * Always allow those to work normally.
       */
      if (!prepend) {
        return originalSetActiveMedia.call(
          this,
          mediaId,
          prepend
        );
      }

      /*
       * Record the current variant's featured media.
       * This is separate from whichever thumbnail
       * the customer has manually selected.
       */
      if (
        !this.trcCurrentVariantFeaturedMediaId
      ) {
        const currentVariantMedia =
          this.elements.viewer.querySelector(
            '[data-media-id].is-active'
          ) ||
          this.elements.viewer.querySelector(
            '[data-media-id]'
          );

        this.trcCurrentVariantFeaturedMediaId =
          currentVariantMedia?.dataset.mediaId ||
          null;
      }

      const featuredMediaChanged =
        this.trcCurrentVariantFeaturedMediaId !==
        mediaId;

      this.trcCurrentVariantFeaturedMediaId =
        mediaId;

      /*
       * Same featured-media ID means only the size
       * changed. Preserve the selected gallery image.
       */
      if (!featuredMediaChanged) {
        return;
      }

      /*
       * A different featured-media ID means the colour
       * changed. Display the new colour's image.
       */
      return originalSetActiveMedia.call(
        this,
        mediaId,
        prepend
      );
    };
})();